import { DISCONNECT_CODE } from './enums';
import Logger from './logger';
import { IS_FIREFOX } from './utils';

type IWebRTCConnectionCallbacks = {
  onTrack?: (track: MediaStreamTrack) => void;
  onError?: (msg: string) => void;
  onSetLocalDescription?: (offer: RTCSessionDescriptionInit) => void;
  onDisconnect?: (reason: { code: DISCONNECT_CODE; msg: string }) => void;
  onConnect?: () => void;
};

type IRTCConnectionConfig = {
  rtcConfig: RTCConfiguration;
  offerConfig;
};

enum ConnectionState {
  DISCONNECTED,
  CONNECTED,
}

const logger = new Logger('WebRTCConnection');

export default class WebRTCConnection {
  private peerConnection: RTCPeerConnection;
  private connectStatus: ConnectionState = ConnectionState.DISCONNECTED;
  private callbacks: IWebRTCConnectionCallbacks;

  constructor(callbacks: IWebRTCConnectionCallbacks = {}) {
    this.callbacks = callbacks;
  }

  initPeerConnection(config: IRTCConnectionConfig) {
    if (this.peerConnection) {
      logger.warn('peerConnection already exist');
      return;
    }

    const rtcConfig = {
      iceServers: [],
      sdpSemantics: 'unified-plan',
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      tcpCandidatePolicy: 'disable',
      IceTransportsType: 'nohost',
      ...(config.rtcConfig ?? {}),
    } as RTCConfiguration;

    try {
      logger.debug('rtcConfig:', rtcConfig);

      this.peerConnection = new RTCPeerConnection(rtcConfig);
    } catch (err) {
      console.warn(err);

      // DOMException: Failed to construct 'RTCPeerConnection': Cannot create so many PeerConnections
      if ('ReferenceError' === err.name && err.message.includes('RTCPeerConnection')) {
        logger.warn('Not Support RTCPeerConnection!');
        this.callbacks.onError?.('Not support RTCPeerConnection');
      } else {
        this.callbacks.onError?.(err?.message || 'Create RTCPeerConnection failed');
      }
      return;
    }

    this.peerConnection.addEventListener('track', this.onTrack);
    this.peerConnection.addEventListener('iceconnectionstatechange', this.onIceConnectionStateChange);
    this.peerConnection.addEventListener('icecandidate', this.onIceCandidate);
    this.peerConnection.addEventListener('signalingstatechange', this.onSignalingStateChange);
    this.peerConnection.addEventListener('connectionstatechange', this.onConnectionStateChange);
    this.peerConnection.addEventListener('negotiationneeded', this.onNegotiationNeeded);

    this.createOffer(config.offerConfig);
  }

  disconnect(reason: { code: DISCONNECT_CODE; msg: string } = {} as any) {
    const { code = DISCONNECT_CODE.MANUAL, msg = '' } = reason;

    logger.warn('disconnect ->', code === 1 ? 'reconnect' : 'manual', msg);

    this.connectStatus = ConnectionState.DISCONNECTED;

    if (this.peerConnection) {
      this.peerConnection.removeEventListener('track', this.onTrack);
      this.peerConnection.removeEventListener('iceconnectionstatechange', this.onIceConnectionStateChange);
      this.peerConnection.removeEventListener('icecandidate', this.onIceCandidate);
      this.peerConnection.removeEventListener('signalingstatechange', this.onSignalingStateChange);
      this.peerConnection.removeEventListener('connectionstatechange', this.onConnectionStateChange);
      this.peerConnection.removeEventListener('negotiationneeded', this.onNegotiationNeeded);
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.callbacks.onDisconnect?.({ code, msg });
  }

  getStats() {
    return this.peerConnection?.getStats();
  }

  private async createOffer(config: RTCOfferOptions & { sdpTransform }) {
    try {
      const { offerToReceiveAudio, offerToReceiveVideo, sdpTransform = (sdp: string) => sdp } = config;
      const enableAddTransceiver = !!this.peerConnection.addTransceiver;
      if (!enableAddTransceiver) {
        logger.warn('PeerConnection not support addTransceiver');
      } else {
        offerToReceiveAudio && this.peerConnection.addTransceiver('audio', {
          direction: 'recvonly',
        });
        offerToReceiveVideo && this.peerConnection.addTransceiver('video', {
          direction: 'recvonly',
        });
      }

      const offerConfig = { voiceActivityDetection: false, ...config };

      logger.debug('offerConfig:', offerConfig);

      // @ts-ignore
      const offer = await this.peerConnection.createOffer(offerConfig);
      logger.debug('createOffer ->');
      logger.debug(offer.sdp);

      // update sdp before setLocalDescription
      if (sdpTransform && typeof sdpTransform === 'function') {
        offer.sdp = sdpTransform(offer.sdp);
      }

      await this.setLocalDescription(offer);
    } catch (err) {
      logger.warn('create offer failed:', err);
      this.disconnect({
        code: DISCONNECT_CODE.RECONNECT,
        msg: 'create offer failed',
      });
      this.callbacks.onError?.('create offer failed');
    }
  }

  private async setLocalDescription(offer: RTCSessionDescriptionInit) {
    try {
      logger.debug('setLocalDescription ->');
      logger.debug(offer.sdp);

      await this.peerConnection.setLocalDescription(offer);
      this.callbacks.onSetLocalDescription?.(offer);
    } catch (err) {
      logger.warn('setLocalDescription failed:', err);
      this.callbacks.onError?.('setLocalDescription failed');
    }
  }

  public async setRemoteDescription(answer: RTCSessionDescriptionInit) {
    try {
      logger.debug('setRemoteDescription ->');
      logger.debug(answer.sdp);
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
    } catch (err) {
      logger.warn('setRemoteDescription failed:', err);
      this.callbacks.onError?.('setRemoteDescription failed');
    }
  }

  private onTrack = (evt: RTCPeerConnectionEventMap['track']) => {
    logger.debug('onTrack ->', evt.track, evt.streams, evt);

    this.callbacks.onTrack?.(evt.track);
  };

  private onIceConnectionStateChange = (evt: RTCPeerConnectionEventMap['iceconnectionstatechange']) => {
    logger.debug('onIceConnectionStateChange ->', this.peerConnection.iceConnectionState, evt);
    switch (this.peerConnection.iceConnectionState) {
      case 'failed':
      case 'disconnected':
        logger.warn('IceConnection disconnected');
        this.disconnect({ code: DISCONNECT_CODE.RECONNECT, msg: 'IceConnection disconnected' });
        break;
      case 'connected':
      case 'completed':
        logger.debug('IceConnection connected');
        if (!IS_FIREFOX) {
          return;
        }
        if (this.connectStatus === ConnectionState.CONNECTED) {
          return;
        }
        this.connectStatus = ConnectionState.CONNECTED;
        this.callbacks.onConnect?.();
        break;
    }
  };

  private onIceCandidate = (evt: RTCPeerConnectionEventMap['icecandidate']) => {
    logger.debug('onIceCandidate ->', evt);
  };

  private onSignalingStateChange = (evt: RTCPeerConnectionEventMap['signalingstatechange']) => {
    logger.debug('onSignalingStateChange ->', this.peerConnection.signalingState, evt);
  };

  private onConnectionStateChange = (evt: RTCPeerConnectionEventMap['connectionstatechange']) => {
    logger.debug('onConnectionStateChange ->', this.peerConnection.connectionState, evt);
    switch (this.peerConnection.connectionState) {
      case 'failed':
      case 'disconnected':
        logger.warn('Connection disconnected');
        this.disconnect({ code: DISCONNECT_CODE.RECONNECT, msg: 'Connection disconnected' });
        break;
      case 'connected':
        logger.debug('Connection connected');
        if (this.connectStatus === ConnectionState.CONNECTED) {
          return;
        }
        this.connectStatus = ConnectionState.CONNECTED;
        this.callbacks.onConnect?.();
        break;
    }
  };

  private onNegotiationNeeded = (evt: RTCPeerConnectionEventMap['negotiationneeded']) => {
    logger.debug('onNegotiationNeeded ->', evt);
  };
}
